import { h, JSX } from 'preact'
import { useMemo } from 'preact/hooks'
import { bem, createUniqueId } from '@nrk/dh-utils'
import { message, Message } from '../../../../lib/intl/Message'
import { Link } from '../Link'
import { createUrlFor } from '../../../routes'
import { Geografi, isGeografi, isParti, Parti } from '../../../domain'
import { useLocale, useLocalizedValue } from '../../../../lib/intl/IntlProvider'
import { ToggleButtonComponent } from '../Toggle/ToggleButton'
import { useCustomContext } from '../../../../lib/context'
import { GA_EVENTS } from '../../../../lib/analytics'
import { Icon } from '../Icons/Icons'
import valgElement from './assets/ValgElement.svg?inline'
import fromEntries from 'object.fromentries'

/* styles */
import s from './NavLinks.module.css'

type Props = {
  grunndata: Array<Geografi | Parti>
  sorter: boolean
  type: 'fylke' | 'kommune' | 'bydel' | 'krets' | 'parti'
}

function getIntArray(str) {
  return [...str.toLowerCase()].map(char => {
    const charCode = char.charCodeAt(0)
    switch (charCode) {
      case 229:
        return 299
      case 248:
        return 298
      case 230:
        return 297
      default:
        return charCode
    }
  })
}

function byName(localize) {
  return (a: Geografi, b: Geografi) => {
    if (a.navn.nb.toLowerCase().indexOf('uoppgitt') !== -1) {
      return 1
    }
    if (b.navn.nb.toLowerCase().indexOf('uoppgitt') !== -1) {
      return -1
    }

    const localizedA = localize(a.navn)
    const localizedB = localize(b.navn)

    const intA = getIntArray(localizedA)
    const intB = getIntArray(localizedB)
    for (let f = 0; f < intA.length; f++) {
      if (intA[f] !== intB[f]) {
        return intA[f] - intB[f]
      }
    }
    return 0
  }
}

export function NavLinks(props: Props) {
  const { grunndata, sorter, type } = props
  const locale = useLocale()
  const localize = useLocalizedValue()
  const selector = useMemo(createUniqueId, [])

  const liste = sorter ? [...grunndata].sort(byName(localize)) : grunndata

  const nrOfItems = grunndata.length
  const modifiers = fromEntries([[type, true]])

  function localizeName(grunndata: Geografi | Parti) {
    if (isGeografi(grunndata) && grunndata.navn.nb.toLowerCase().indexOf('uoppgitt') !== -1) {
      return grunndata.type === 'Bydel' ? message('uoppgitt-bydel') : message('uoppgitt-krets')
    } else {
      return localize(grunndata.navn)
    }
  }

  return (
    <div class={s.navLinks} data-expand-selector={selector}>
      <div class={s.links}>
        {liste.map((grunndata, idx) => {
          return (
            <Link
              linkClickType="NAV"
              href={createUrlFor(grunndata, locale)}
              style={{ '--item-index': idx + 1 }}
            >
              {isParti(grunndata) && (
                <span
                  dangerouslySetInnerHTML={{ __html: valgElement }}
                  class={s.partiElement}
                  style={{ color: grunndata.farge }}
                />
              )}
              <span class={s.tekst}>{localizeName(grunndata)}</span>
              <svg
                version="1.1"
                xmlns="http://www.w3.org/2000/svg"
                xmlnsXlink="http://www.w3.org/1999/xlink"
                viewBox="0 0 15 15"
                width={15}
                height={15}
                class={s.pil}
              >
                <polyline points="7,1.5 13,7.5 7,13.5 " />
                <line x1="2" y1="7.5" x2="13" y2="7.5" />
              </svg>
            </Link>
          )
        })}
      </div>
      <div class={bem(s.footer, { hasMore5: nrOfItems > 5, hasMore10: nrOfItems > 10 })}>
        <ExpandButton
          selector={`[data-expand-selector="${selector}"]`}
          classToToggle={s.navLinksShowMore}
          class="nrk-button"
        >
          <span>
            <Message id="lenker-vis-alle" modifiers={modifiers} />
            <Icon type="nrk-chevron-down" />
          </span>
          <span hidden>
            <Message id="lenker-vis-færre" modifiers={modifiers} />
            <Icon type="nrk-chevron-up" />
          </span>
        </ExpandButton>
      </div>
    </div>
  )
}

interface ExpandButtonProps extends JSX.HTMLAttributes {
  selector: string
  classToToggle: string
  isCustomElementDisabled?: boolean
}

function ExpandButton({ children, ...rest }: ExpandButtonProps) {
  return <ToggleButtonComponent {...rest}>{children}</ToggleButtonComponent>
}
